/**
 * Paylaşılan Base + Reference katman tanımları.
 * İlçe/mahalle sınırları HER analizde yeniden oluşturulmaz — tek Base owner.
 */
import {
  EMPTY_FC,
  GTFS_MAP_LAYERS,
  GTFS_MAP_SOURCES,
  LAYER_CATEGORIES,
  LAYER_OWNERS,
  ROUTE_ARROW_IMAGE,
  ROUTE_SOURCE_TOLERANCE,
} from './gtfsMapLayers.js'
import {
  MAP_FONT_MEDIUM,
  MAP_FONT_REGULAR,
  getActiveMapTheme,
  themePaint,
} from './gtfsBasemap.js'
import { SELECTED_STOP_COLOR } from './routeStyle.js'
import { useMapLayerStore } from '@/stores/mapLayer.store'
import { attachMap, getAttachedMap } from './mapLayerRegistry.js'

/** Analiz açıkken ağın opaklık çarpanı */
export const ANALYSIS_REFERENCE_DIM = 0.4

const L = GTFS_MAP_LAYERS

const isSelected = ['==', ['get', 'selected'], 1]
const isMulti = ['==', ['get', 'multi'], 1]
const routeState = ['coalesce', ['get', 'state'], 0]

/**
 * Tema + analiz durumuna göre referans katman boyaları.
 * Hat durumu (`state`): 0 normal, 1 seçili hat (özet çizgisi gizli), 2 sönük.
 */
export function referencePaint({ theme, analysisActive = false } = {}) {
  const t = themePaint(theme || getActiveMapTheme())
  const k = analysisActive ? ANALYSIS_REFERENCE_DIM : 1
  return {
    [L.routeLine0]: {
      'line-opacity': [
        'case',
        ['==', routeState, 1],
        0,
        ['==', routeState, 2],
        0.24 * k,
        0.92 * k,
      ],
    },
    [L.routeCasing]: {
      'line-color': t.routeCasing,
      'line-opacity': analysisActive ? 0.5 : 0.95,
    },
    [L.routeLine1]: {
      'line-opacity': analysisActive ? 0.7 : 1,
    },
    [L.routeArrows]: {
      'icon-opacity': analysisActive ? 0.6 : 1,
    },
    [L.routeLabel]: {
      'text-color': ['get', 'color'],
      'text-halo-color': t.labelHalo,
      'text-opacity': analysisActive ? 0.5 : 1,
    },
    [L.stopsCircle]: {
      'circle-color': ['case', isSelected, SELECTED_STOP_COLOR, t.stopFill],
      'circle-stroke-color': [
        'case',
        isSelected,
        '#ffffff',
        isMulti,
        t.stopNeutral,
        ['coalesce', ['get', 'color'], t.stopNeutral],
      ],
      'circle-opacity': k,
      'circle-stroke-opacity': k,
    },
    [L.stopsSelected]: {
      'circle-opacity': 0.22,
    },
    [L.stopsLabel]: {
      'text-color': t.labelText,
      'text-halo-color': t.labelHalo,
      'text-opacity': analysisActive ? 0.55 : 1,
    },
    [L.populationCircle]: {
      'circle-color': t.populationFill,
      'circle-stroke-color': t.populationStroke,
    },
    [L.baseIlceFill]: { 'fill-color': t.baseFill },
    [L.baseMahalleFill]: { 'fill-color': t.baseFill },
    [L.baseIlceLine]: { 'line-color': t.baseIlceLine },
    [L.baseMahalleLine]: { 'line-color': t.baseMahalleLine },
    [L.selectedFeatureFill]: { 'fill-color': t.selectedFill },
    [L.selectedFeatureLine]: { 'line-color': t.selectedLine },
    [L.hoverFeatureLine]: { 'line-color': t.hoverLine },
  }
}

export function applyReferencePaint(map, opts = {}) {
  const target = map || getAttachedMap()
  if (!target?.getLayer) return
  const paint = referencePaint(opts)
  for (const [layerId, props] of Object.entries(paint)) {
    if (!target.getLayer(layerId)) continue
    for (const [name, value] of Object.entries(props)) {
      try {
        target.setPaintProperty(layerId, name, value)
      } catch {
        /* ignore */
      }
    }
  }
}

/** Yön oku (sağa bakan şerit oku) — çizgi yönünde döner */
export function createRouteArrowImage(size = 24) {
  const data = new Uint8Array(size * size * 4)
  const s = size / 24
  const segs = [
    [8 * s, 6 * s, 15.5 * s, 12 * s],
    [15.5 * s, 12 * s, 8 * s, 18 * s],
  ]
  const half = 1.9 * s
  for (let y = 0; y < size; y += 1) {
    for (let x = 0; x < size; x += 1) {
      let d = Infinity
      for (const [x1, y1, x2, y2] of segs) {
        const dx = x2 - x1
        const dy = y2 - y1
        const tt = Math.max(
          0,
          Math.min(1, ((x + 0.5 - x1) * dx + (y + 0.5 - y1) * dy) / (dx * dx + dy * dy))
        )
        const px = x1 + tt * dx - (x + 0.5)
        const py = y1 + tt * dy - (y + 0.5)
        d = Math.min(d, Math.hypot(px, py))
      }
      const a = Math.max(0, Math.min(1, half + 0.5 - d))
      const i = (y * size + x) * 4
      data[i] = 255
      data[i + 1] = 255
      data[i + 2] = 255
      data[i + 3] = Math.round(a * 255)
    }
  }
  return { width: size, height: size, data }
}

export function ensureRouteArrowImage(map) {
  if (!map?.hasImage || map.hasImage(ROUTE_ARROW_IMAGE)) return
  try {
    map.addImage(ROUTE_ARROW_IMAGE, createRouteArrowImage(24), { pixelRatio: 2 })
  } catch {
    /* ignore */
  }
}

/**
 * Katman sırası: analiz dolguları < seçim/hover < ağ < analiz noktaları/çizgileri < alan < etiketler.
 */
const REFERENCE_ORDER = [
  L.selectedFeatureFill,
  L.selectedFeatureLine,
  L.hoverFeatureLine,
  L.populationCircle,
  L.routeLine0,
  L.routeCasing,
  L.routeLine1,
  L.routeArrows,
  L.routeHit,
  L.stopsSelected,
  L.stopsCircle,
  L.stopsHit,
  L.corridorLine,
  L.corridorHit,
  L.saStopsCircle,
  L.saStopsHit,
  L.corridorAreaFill,
  L.corridorAreaLine,
  L.adminMahalleLabel,
  L.adminIlceLabel,
  L.routeLabel,
  L.stopsLabel,
]

export function raiseReferenceLayers(map) {
  const target = map || getAttachedMap()
  if (!target?.moveLayer) return
  for (const id of REFERENCE_ORDER) {
    if (!target.getLayer(id)) continue
    try {
      target.moveLayer(id)
    } catch {
      /* ignore */
    }
  }
}

/**
 * Harita load sonrası çağrılır — Base + Reference + Interaction iskeleti.
 * @param {import('maplibre-gl').Map} map
 */
export function bootstrapSharedMapLayers(map) {
  if (!map) return
  attachMap(map)
  ensureRouteArrowImage(map)
  registerBaseAndReferenceLayers()
  registerInteractionLayers()
  raiseReferenceLayers(map)
}

export function registerBaseAndReferenceLayers() {
  const store = useMapLayerStore()
  const paint = referencePaint()

  // --- Base: İlçe ---
  store.registerLayer(
    LAYER_CATEGORIES.base,
    LAYER_OWNERS.shared,
    'ilce-boundaries',
    {
      sourceId: GTFS_MAP_SOURCES.baseIlce,
      source: { type: 'geojson', data: EMPTY_FC },
      layers: [
        {
          id: L.baseIlceFill,
          type: 'fill',
          paint: {
            ...paint[L.baseIlceFill],
            'fill-opacity': 1,
          },
        },
        {
          id: L.baseIlceLine,
          type: 'line',
          paint: {
            ...paint[L.baseIlceLine],
            'line-width': ['interpolate', ['linear'], ['zoom'], 9, 0.8, 13, 1.4],
            'line-opacity': 1,
          },
        },
      ],
    },
    {
      visible: true,
      locked: true,
      opacity: 1,
      opacityPaintProp: 'line-opacity',
      label: 'İlçe Sınırları',
    }
  )

  // --- Base: Mahalle ---
  store.registerLayer(
    LAYER_CATEGORIES.base,
    LAYER_OWNERS.shared,
    'mahalle-boundaries',
    {
      sourceId: GTFS_MAP_SOURCES.baseMahalle,
      source: { type: 'geojson', data: EMPTY_FC },
      layers: [
        {
          id: L.baseMahalleFill,
          type: 'fill',
          paint: {
            ...paint[L.baseMahalleFill],
            'fill-opacity': 1,
          },
        },
        {
          id: L.baseMahalleLine,
          type: 'line',
          minzoom: 11,
          paint: {
            ...paint[L.baseMahalleLine],
            'line-width': 0.6,
            'line-opacity': 1,
          },
        },
      ],
    },
    {
      visible: true,
      locked: true,
      opacity: 0.85,
      opacityPaintProp: 'line-opacity',
      label: 'Mahalle Sınırları',
    }
  )

  // --- Reference: Duraklar / Hatlar (mevcut source id'leri ile) ---
  store.registerLayer(
    LAYER_CATEGORIES.reference,
    LAYER_OWNERS.shared,
    'stops',
    {
      sourceId: GTFS_MAP_SOURCES.stops,
      source: { type: 'geojson', data: EMPTY_FC },
      layers: [
        {
          id: L.stopsHit,
          type: 'circle',
          paint: {
            'circle-radius': 11,
            'circle-color': '#000000',
            'circle-opacity': 0,
          },
        },
        {
          id: L.stopsSelected,
          type: 'circle',
          filter: isSelected,
          paint: {
            ...paint[L.stopsSelected],
            'circle-radius': ['interpolate', ['linear'], ['zoom'], 10, 11, 16, 17],
            'circle-color': SELECTED_STOP_COLOR,
            'circle-stroke-width': 1.5,
            'circle-stroke-color': SELECTED_STOP_COLOR,
            'circle-stroke-opacity': 0.7,
          },
        },
        {
          id: L.stopsCircle,
          type: 'circle',
          paint: {
            ...paint[L.stopsCircle],
            'circle-radius': [
              'interpolate',
              ['linear'],
              ['zoom'],
              9,
              ['case', isSelected, 6, 1.6],
              12,
              ['case', isSelected, 7, 2.8],
              14,
              ['case', isSelected, 8, 4.2],
              17,
              ['case', isSelected, 10, 6.5],
            ],
            'circle-stroke-width': [
              'interpolate',
              ['linear'],
              ['zoom'],
              9,
              ['case', isSelected, 2.5, 0.9],
              13,
              ['case', isSelected, 3, 1.8],
              16,
              ['case', isSelected, 3, 2.6],
            ],
          },
        },
        {
          id: L.stopsLabel,
          type: 'symbol',
          minzoom: 14.5,
          layout: {
            'text-field': ['get', 'stop_name'],
            'text-font': MAP_FONT_REGULAR,
            'text-size': ['interpolate', ['linear'], ['zoom'], 14.5, 10.5, 18, 13],
            'text-anchor': 'top',
            'text-offset': [0, 0.85],
            'text-max-width': 9,
            'text-optional': true,
            'text-padding': 3,
            'symbol-sort-key': ['case', isSelected, 0, isMulti, 1, 2],
          },
          paint: {
            ...paint[L.stopsLabel],
            'text-halo-width': 1.4,
          },
        },
      ],
    },
    {
      visible: true,
      label: 'Duraklar',
      opacityPaintProp: 'circle-opacity',
    }
  )

  store.registerLayer(
    LAYER_CATEGORIES.reference,
    LAYER_OWNERS.shared,
    'routes',
    {
      sourceId: GTFS_MAP_SOURCES.routeLines,
      source: {
        type: 'geojson',
        data: EMPTY_FC,
        tolerance: ROUTE_SOURCE_TOLERANCE,
      },
      layers: [
        {
          id: L.routeLine0,
          type: 'line',
          filter: ['!=', ['coalesce', ['get', 'focus'], 0], 1],
          paint: {
            ...paint[L.routeLine0],
            'line-color': ['coalesce', ['get', 'color'], '#64748b'],
            'line-width': [
              'interpolate',
              ['linear'],
              ['zoom'],
              9,
              1.4,
              12,
              2.6,
              14,
              3.6,
              17,
              6,
            ],
          },
          layout: {
            'line-join': 'round',
            'line-cap': 'round',
            'line-sort-key': ['case', ['==', routeState, 2], 0, 1],
          },
        },
        {
          id: L.routeCasing,
          type: 'line',
          filter: ['==', ['get', 'focus'], 1],
          paint: {
            ...paint[L.routeCasing],
            'line-width': [
              'interpolate',
              ['linear'],
              ['zoom'],
              9,
              5,
              12,
              7.5,
              14,
              9.5,
              17,
              13,
            ],
            'line-offset': ['interpolate', ['linear'], ['zoom'], 12, 0, 14, 2.5, 17, 5],
          },
          layout: { 'line-join': 'round', 'line-cap': 'round' },
        },
        {
          id: L.routeLine1,
          type: 'line',
          filter: ['==', ['get', 'focus'], 1],
          paint: {
            ...paint[L.routeLine1],
            'line-color': ['coalesce', ['get', 'color'], '#2563eb'],
            'line-width': [
              'interpolate',
              ['linear'],
              ['zoom'],
              9,
              3,
              12,
              4.5,
              14,
              6,
              17,
              9,
            ],
            'line-offset': ['interpolate', ['linear'], ['zoom'], 12, 0, 14, 2.5, 17, 5],
          },
          layout: { 'line-join': 'round', 'line-cap': 'round' },
        },
        {
          id: L.routeArrows,
          type: 'symbol',
          filter: ['==', ['get', 'focus'], 1],
          minzoom: 11.5,
          layout: {
            'symbol-placement': 'line',
            'symbol-spacing': ['interpolate', ['linear'], ['zoom'], 12, 70, 16, 110],
            'icon-image': ROUTE_ARROW_IMAGE,
            'icon-size': ['interpolate', ['linear'], ['zoom'], 12, 0.75, 16, 1],
            'icon-allow-overlap': true,
            'icon-ignore-placement': true,
            'icon-rotation-alignment': 'map',
          },
          paint: { ...paint[L.routeArrows] },
        },
        {
          id: L.routeHit,
          type: 'line',
          paint: {
            'line-color': '#000000',
            'line-width': 12,
            'line-opacity': 0,
          },
          layout: { 'line-join': 'round', 'line-cap': 'round' },
        },
        {
          id: L.routeLabel,
          type: 'symbol',
          minzoom: 10.5,
          filter: [
            'all',
            ['!=', ['coalesce', ['get', 'focus'], 0], 1],
            ['!=', routeState, 2],
          ],
          layout: {
            'symbol-placement': 'line',
            'symbol-spacing': 320,
            'text-field': ['get', 'label'],
            'text-font': MAP_FONT_MEDIUM,
            'text-size': ['interpolate', ['linear'], ['zoom'], 10.5, 11, 15, 13],
            'text-max-angle': 30,
            'text-keep-upright': true,
            'text-padding': 6,
            'text-letter-spacing': 0.02,
          },
          paint: {
            ...paint[L.routeLabel],
            'text-halo-width': 2,
          },
        },
      ],
    },
    {
      visible: true,
      label: 'Hatlar',
      opacityPaintProp: 'line-opacity',
    }
  )

  store.registerLayer(
    LAYER_CATEGORIES.reference,
    LAYER_OWNERS.shared,
    'population',
    {
      sourceId: GTFS_MAP_SOURCES.populationPoints,
      source: { type: 'geojson', data: EMPTY_FC },
      layers: [
        {
          id: L.populationCircle,
          type: 'circle',
          paint: {
            ...paint[L.populationCircle],
            'circle-radius': [
              'interpolate',
              ['linear'],
              ['coalesce', ['get', 'population'], 0],
              0,
              2,
              500,
              4,
              2000,
              7,
            ],
            'circle-stroke-width': 0.8,
            'circle-opacity': 0.6,
          },
        },
      ],
    },
    {
      visible: false,
      label: 'Nüfus Noktaları',
      opacityPaintProp: 'circle-opacity',
    }
  )

}

/** Interaction — analiz değişiminde temizlenir; yeniden kaydedilir */
export function registerInteractionLayers() {
  const store = useMapLayerStore()
  const paint = referencePaint()

  store.registerLayer(
    LAYER_CATEGORIES.interaction,
    LAYER_OWNERS.shared,
    'selected-feature',
    {
      sourceId: GTFS_MAP_SOURCES.selectedFeature,
      source: { type: 'geojson', data: EMPTY_FC },
      layers: [
        {
          id: L.selectedFeatureFill,
          type: 'fill',
          paint: {
            ...paint[L.selectedFeatureFill],
            'fill-opacity': 1,
          },
        },
        {
          id: L.selectedFeatureLine,
          type: 'line',
          paint: {
            ...paint[L.selectedFeatureLine],
            'line-width': 2.4,
            'line-opacity': 1,
          },
        },
      ],
    },
    { visible: true, label: 'Seçili Öğe' }
  )

  store.registerLayer(
    LAYER_CATEGORIES.interaction,
    LAYER_OWNERS.shared,
    'hover-feature',
    {
      sourceId: GTFS_MAP_SOURCES.hoverFeature,
      source: { type: 'geojson', data: EMPTY_FC },
      layers: [
        {
          id: L.hoverFeatureLine,
          type: 'line',
          paint: {
            ...paint[L.hoverFeatureLine],
            'line-width': 1.6,
            'line-opacity': 0.9,
          },
        },
      ],
    },
    { visible: true, label: 'Hover' }
  )
  raiseReferenceLayers()
}

/**
 * Base sınır GeoJSON uygula.
 */
export function applyBaseBoundaries({ ilceFc, mahalleFc } = {}) {
  const store = useMapLayerStore()
  if (ilceFc) {
    store.setSourceData(
      LAYER_CATEGORIES.base,
      LAYER_OWNERS.shared,
      'ilce-boundaries',
      ilceFc
    )
  }
  if (mahalleFc) {
    store.setSourceData(
      LAYER_CATEGORIES.base,
      LAYER_OWNERS.shared,
      'mahalle-boundaries',
      mahalleFc
    )
  }
}
